import React, { useEffect, useRef, useState } from 'react';
import messageService from '../../services/messageService';
import { T, RADIUS, BODY_FONT } from './messagesModel';
import { riseIn } from './messagesMotion';
import { formatStamp, currentParagraph, TRANSCRIPT_POLL_MS, TRANSCRIPT_POLL_MAX_MS } from './voiceNoteModel';

/**
 * The note's words, in timestamped paragraphs inside a scrolling panel. While the worker is
 * still writing it the panel says so and asks again every few seconds; a failure offers RETRY.
 *
 * Two homes, one component (Oct 13 2026):
 *  - `inline` — attached INSIDE something that already has a frame: the voice bubble's own box
 *    (the TRANSCRIPT button on the card opens it there) or the dock, dropped out of the player.
 *    No card of its own, no shadow, full width of whatever holds it.
 *  - the standalone card, as it was.
 *
 * `onSeek` is what makes a paragraph tappable. Reading a note you are NOT listening to passes
 * none, and the rows are then plain text — the whole point of the button on the bubble is to
 * read it without playing anything, so a stray tap must not start the audio.
 *
 * `at` is the player's position; pass a negative one (or nothing) when no player is on this
 * note, and no paragraph lights up.
 */
export default function VoiceTranscript({ msg, at = -1, onSeek, onClose, ephemeral = false, inline = false, maxHeight = 240 }) {
  const [state, setState] = useState({ status: ephemeral ? 'pending' : 'loading', paragraphs: [], error: null });
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const startedAt = useRef(Date.now());

  useEffect(() => { riseIn(rootRef.current, { dy: 8 }); }, []);

  // Fetch, then poll while pending — the note was probably transcribed before it was tapped.
  // RETRY bumps `round`, which re-arms the same loop.
  const [round, setRound] = useState(0);
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    if (ephemeral) return undefined;
    let alive = true;
    let timer = null;
    const load = async () => {
      const r = await messageService.getVoiceTranscript?.(msg.id);
      if (!alive) return;
      if (!r?.success || !r.data) { setState({ status: 'failed', paragraphs: [], error: r?.error?.message || r?.message || 'Could not load the transcript.' }); return; }
      const d = r.data;
      if (d.status === 'pending') {
        if (Date.now() - startedAt.current > TRANSCRIPT_POLL_MAX_MS) { setState({ status: 'failed', paragraphs: [], error: 'This is taking too long.' }); return; }
        setState({ status: 'pending', paragraphs: [], error: null });
        timer = setTimeout(load, TRANSCRIPT_POLL_MS);
        return;
      }
      setState({ status: d.status, paragraphs: d.paragraphs || [], error: d.error || null, text: d.text || '', language: d.language || null });
    };
    load();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [msg.id, ephemeral, round]);

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    const r = await messageService.retryVoiceTranscript?.(msg.id);
    setRetrying(false);
    if (!r?.success) return;
    startedAt.current = Date.now();
    setState({ status: 'pending', paragraphs: [], error: null });
    setRound((k) => k + 1);
  };

  const cur = currentParagraph(state.paragraphs, at);
  // Keep the lit paragraph in view as the note plays, without fighting a reader who scrolled.
  const userScrolled = useRef(false);
  useEffect(() => {
    if (cur < 0 || userScrolled.current) return;
    const el = listRef.current?.querySelector(`[data-para="${cur}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [cur]);

  const total = state.paragraphs.length ? state.paragraphs[state.paragraphs.length - 1].endSec : msg.audioDurationSec || 0;

  return (
    <div
      ref={rootRef}
      data-voice-transcript
      data-status={state.status}
      role="region"
      aria-label="Transcript"
      onClick={(e) => e.stopPropagation()}
      style={inline
        ? { marginTop: 10, width: '100%', borderTop: `2px solid ${T.off}`, paddingTop: 2, background: 'transparent' }
        : { marginTop: 6, width: 340, maxWidth: '100%', background: T.white, borderRadius: RADIUS.bubble, boxShadow: '0 4px 18px rgba(0,0,0,.08)', overflow: 'hidden' }}
    >
      <div className="flex items-center" style={{ padding: inline ? '8px 2px 4px' : '10px 14px 6px', gap: 8 }}>
        <span className="flex-1 font-mono font-bold truncate" style={{ fontSize: 10, letterSpacing: '.1em' }}>
          TRANSCRIPT{total ? ` · ${formatStamp(total)}` : ''}{state.language && state.language !== 'english' ? ` · ${state.language.toUpperCase()}` : ''}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide transcript"
            className="flex items-center justify-center flex-shrink-0 font-mono font-bold"
            style={{ width: 24, height: 24, borderRadius: '50%', border: 0, background: T.off, color: T.ink, fontSize: 12, cursor: 'pointer' }}
          >
            ×
          </button>
        )}
      </div>

      {state.status === 'loading' && <Line inline={inline}>LOADING…</Line>}
      {state.status === 'pending' && (
        <div className="flex items-center" style={{ padding: inline ? '2px 2px 10px' : '4px 14px 14px', gap: 8 }}>
          <span aria-hidden className="inline-block flex-shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-black" style={{ width: 12, height: 12 }} />
          <span className="font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>{ephemeral ? 'SENDING FIRST…' : 'WRITING IT DOWN…'}</span>
        </div>
      )}
      {(state.status === 'failed' || state.status === 'unavailable') && (
        <div style={{ padding: inline ? '2px 2px 10px' : '2px 14px 14px' }}>
          <div className="font-mono" style={{ fontSize: 11, lineHeight: 1.5, color: T.grey600 }}>{state.error || "Couldn't transcribe this one."}</div>
          {state.status === 'failed' && (
            <button
              type="button"
              onClick={retry}
              disabled={retrying}
              className="font-mono font-bold disabled:opacity-40"
              style={{ marginTop: 8, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: 'transparent', color: T.ink, fontSize: 10, letterSpacing: '.1em', padding: '5px 12px', cursor: 'pointer' }}
            >
              RETRY
            </button>
          )}
        </div>
      )}
      {state.status === 'done' && (
        state.paragraphs.length === 0 ? <Line inline={inline}>NOTHING WAS SAID.</Line> : (
          <div
            ref={listRef}
            data-voice-transcript-body
            onScroll={() => { userScrolled.current = true; }}
            className="overflow-y-auto"
            style={{ maxHeight, padding: inline ? '0 0 10px' : '0 10px 12px', display: 'flex', flexDirection: 'column', gap: 4, overscrollBehavior: 'contain' }}
          >
            {state.paragraphs.map((p, i) => {
              const row = (
                <>
                  <span className="flex-shrink-0 font-mono font-bold tabular-nums" style={{ fontSize: 9, letterSpacing: '.08em', color: i === cur ? T.ink : T.grey500, paddingTop: 3 }}>{formatStamp(p.startSec)}</span>
                  <span style={{ fontSize: 14, lineHeight: 1.45, fontFamily: BODY_FONT, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.text}</span>
                </>
              );
              const base = { gap: 8, padding: '6px 6px', border: 0, borderRadius: 12, background: i === cur ? T.activeBadge : 'transparent', color: T.ink, transition: 'background-color 160ms' };
              return onSeek ? (
                <button
                  key={`${p.startSec}-${i}`}
                  type="button"
                  data-para={i}
                  data-current={i === cur || undefined}
                  onClick={() => onSeek(p.startSec)}
                  aria-label={`Play from ${formatStamp(p.startSec)}`}
                  className="flex items-start text-left"
                  style={{ ...base, cursor: 'pointer' }}
                >
                  {row}
                </button>
              ) : (
                <div key={`${p.startSec}-${i}`} data-para={i} data-current={i === cur || undefined} className="flex items-start text-left" style={base}>
                  {row}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

function Line({ children, inline }) {
  return <div className="font-mono font-bold" style={{ padding: inline ? '2px 2px 10px' : '2px 14px 14px', fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>{children}</div>;
}
